'use client';

import _slicedToArray from "D:\\CODE\\caixm\\web\\caixm-portal\\node_modules\\babel-preset-react-app\\node_modules\\@babel\\runtime/helpers/esm/slicedToArray";
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import hotkeys from 'hotkeys-js';
import { setupHighlighter, getElementCodeInfo, gotoServerEditor, getElementInspect } from './utils';
import { useLayoutEffect, useEffectEvent, useMousePosition } from './hooks';
import { Overlay } from './Overlay';
/**
 * `v2.0.0` changes:
 *   - make 'Ctrl + Shift + Alt + C' as default shortcut on Windows/Linux
 *   - export `defaultHotkeys`
 */

export var defaultHotkeys = function defaultHotkeys() {
  var _a;

  return ((_a = navigator.platform) === null || _a === void 0 ? void 0 : _a.startsWith('Mac')) ? ['Ctrl', 'Shift', 'Command', 'C'] : ['Ctrl', 'Shift', 'Alt', 'C'];
};
export var Inspector = function Inspector(props) {
  var keys = props.keys,
      onHoverElement = props.onHoverElement,
      onClickElement = props.onClickElement,
      onInspectElement = props.onInspectElement,
      controlledActive = props.active,
      onActiveChange = props.onActiveChange,
      disableLaunchEditor = props.disableLaunchEditor,
      _props$disable = props.disable,
      disable = _props$disable === void 0 ? process.env.NODE_ENV !== 'development' : _props$disable,
      children = props.children;

  var _useState = useState(controlledActive !== null && controlledActive !== void 0 ? controlledActive : false),
      _useState2 = _slicedToArray(_useState, 2),
      isActive = _useState2[0],
      setActive = _useState2[1]; // sync state as controlled component


  useLayoutEffect(function () {
    if (controlledActive !== undefined) {
      setActive(controlledActive);
    }
  }, [controlledActive]);
  useEffect(function () {
    isActive ? startInspect() : stopInspect();
    return stopInspect;
  }, [isActive]); // hotkeys-js params need string

  var hotkey = keys === null ? null : (keys !== null && keys !== void 0 ? keys : []).join('+');
  /** inspector tooltip overlay */

  var overlayRef = useRef();
  var mouseRef = useMousePosition({
    disable: disable
  });
  var activate = useEffectEvent(function () {
    onActiveChange === null || onActiveChange === void 0 ? void 0 : onActiveChange(true);

    if (controlledActive === undefined) {
      setActive(true);
    }
  });
  var deactivate = useEffectEvent(function () {
    onActiveChange === null || onActiveChange === void 0 ? void 0 : onActiveChange(false);

    if (controlledActive === undefined) {
      setActive(false);
    }
  });
  var startInspect = useEffectEvent(function () {
    if (overlayRef.current || disable) return;
    var overlay = new Overlay();
    overlayRef.current = overlay;
    hotkeys("esc", deactivate);
    var stopCallback = setupHighlighter({
      onPointerOver: handleHoverElement,
      onClick: handleClickElement
    });
    overlay.setRemoveCallback(stopCallback); // inspect element immediately at mouse point

    var initPoint = mouseRef.current;
    var initElement = document.elementFromPoint(initPoint.x, initPoint.y);
    if (initElement) handleHoverElement(initElement);
  });
  var stopInspect = useEffectEvent(function () {
    var _a;

    (_a = overlayRef.current) === null || _a === void 0 ? void 0 : _a.remove();
    overlayRef.current = undefined;
    hotkeys.unbind("esc", deactivate);
  });
  var handleHoverElement = useEffectEvent(function (element) {
    var _a;

    var overlay = overlayRef.current;
    var codeInfo = getElementCodeInfo(element);
    var relativePath = codeInfo === null || codeInfo === void 0 ? void 0 : codeInfo.relativePath;
    var absolutePath = codeInfo === null || codeInfo === void 0 ? void 0 : codeInfo.absolutePath;

    var _getElementInspect = getElementInspect(element),
        fiber = _getElementInspect.fiber,
        name = _getElementInspect.name,
        title = _getElementInspect.title;

    (_a = overlay === null || overlay === void 0 ? void 0 : overlay.inspect) === null || _a === void 0 ? void 0 : _a.call(overlay, [element], title, relativePath !== null && relativePath !== void 0 ? relativePath : absolutePath);
    onHoverElement === null || onHoverElement === void 0 ? void 0 : onHoverElement({
      element: element,
      fiber: fiber,
      codeInfo: codeInfo,
      name: name
    });
  });
  var handleClickElement = useEffectEvent(function (element) {
    deactivate();
    var codeInfo = getElementCodeInfo(element);

    var _getElementInspect2 = getElementInspect(element),
        fiber = _getElementInspect2.fiber,
        name = _getElementInspect2.name;

    onClickElement === null || onClickElement === void 0 ? void 0 : onClickElement({
      element: element,
      fiber: fiber,
      codeInfo: codeInfo,
      name: name
    });

    if (fiber && codeInfo) {
      onInspectElement === null || onInspectElement === void 0 ? void 0 : onInspectElement({
        element: element,
        fiber: fiber,
        codeInfo: codeInfo,
        name: name
      });

      if (!onInspectElement && !disableLaunchEditor) {
        gotoServerEditor(codeInfo);
      }
    }
  });
  useEffect(function () {
    var handleHotKeys = function handleHotKeys() {
      overlayRef.current ? deactivate() : activate();
    };

    var bindKey = hotkey === null || disable ? null : hotkey || defaultHotkeys().join('+');

    if (bindKey) {
      // https://github.com/jaywcjlove/hotkeys
      hotkeys(bindKey, handleHotKeys);
      return function () {
        hotkeys.unbind(bindKey, handleHotKeys);
      };
    }
  }, [hotkey, disable]);
  return _jsx(_Fragment, {
    children: children !== null && children !== void 0 ? children : null
  });
};